import { Link } from 'react-router-dom'

export default function ErrorsGuide() {
  return (
    <div>
      <h1>Errors</h1>
      <p>
        The API uses standard HTTP response codes. Successful responses are{' '}
        <code>2xx</code>; client errors are <code>4xx</code>; server errors are{' '}
        <code>5xx</code>.
      </p>
      <p>
        <em>
          If you see a 5xx, that's us. If you see a 4xx, that's almost
          certainly the request. We mean this in the kindest possible way.
        </em>
      </p>

      <h2>Response shape</h2>
      <p>Error responses are JSON with a <code>detail</code> field:</p>
      <pre>
        <code>{`{
  "detail": "Insufficient permissions"
}`}</code>
      </pre>
      <p>
        Unhandled server errors include a <code>request_id</code> field that
        you can quote to support for log correlation.
      </p>

      <h2>Status codes</h2>
      <ul>
        <li>
          <code>200 OK</code> — request succeeded with a response body.
        </li>
        <li>
          <code>201 Created</code> — resource was created.
        </li>
        <li>
          <code>204 No Content</code> — request succeeded; no body.
        </li>
        <li>
          <code>400 Bad Request</code> — malformed request (e.g. invalid JSON).
        </li>
        <li>
          <code>401 Unauthorized</code> — missing, malformed, or revoked API key.
        </li>
        <li>
          <code>403 Forbidden</code> — your key lacks the required permission,
          or the resource belongs to a different org. See{' '}
          <Link to="/guides/authentication">Authentication</Link> for the RBAC
          model.
        </li>
        <li>
          <code>404 Not Found</code> — the resource doesn't exist.
        </li>
        <li>
          <code>409 Conflict</code> — request conflicts with current state
          (e.g. revoking an already-revoked key).
        </li>
        <li>
          <code>422 Unprocessable Entity</code> — body or query params failed
          validation. The response body lists the field-level errors.
        </li>
        <li>
          <code>429 Too Many Requests</code> — rate limit exceeded. See{' '}
          <Link to="/guides/rate-limits">Rate limits</Link>.
        </li>
        <li>
          <code>500 Internal Server Error</code> — something broke server-side.
          Retry safe-to-retry requests with exponential backoff; otherwise
          contact support with the <code>request_id</code>.
        </li>
      </ul>

      <h2>Retrying</h2>
      <p>
        It's safe to retry idempotent requests (<code>GET</code>, <code>PUT</code>,
        <code>DELETE</code>) and most <code>5xx</code> failures. For{' '}
        <code>POST</code> mutations, retry only when you know the request is
        idempotent (most aren't).
      </p>
      <p>
        Suggested backoff: 200ms × 2^n with jitter, max 5 attempts.
      </p>
    </div>
  )
}
